import { useTranslation } from "react-i18next";
import QuickViewPanelWrapper from "../../../../components/quick-view-panel-wrapper/QuickViewPanelWrapper";
import { PostDischargeInfo } from "../../../../models/post-discharge-info.model";
import { Formatter } from "../../../../services/formatter/formatter.service";
import { CareTransitionEvent, toCareTransitionEvents } from "../../../../utils/care-transitions";

/**
 * The Care Transitions quick-view card (KAN-2551).
 *
 * A short fixed header (latest reason for admission, days since discharge — both summary
 * values, not events) above a FLAT, newest-first list of the participant's most recent
 * care-transition events. Cardiac Rehab Start/Completion are just two more event types;
 * they need no special-casing here. The full history lives in the modal.
 *
 * Note the panel/modal titles are still the `POST_DISCHARGE` i18n keys — only the rendered
 * strings changed, so a future rename stays a one-line edit and nothing downstream
 * (`user-state`, task rules, dbt) is disturbed.
 */
const PostDischargeQuickViewPanel = (props: {
	isLoading: boolean;
	isError: boolean;
	postDischargeInfo: PostDischargeInfo | undefined;
	events: CareTransitionEvent[] | undefined;
	onClickAction: () => void;
}) => {
	const { t } = useTranslation("common");

	const events = toCareTransitionEvents(props.events ?? []);

	/** Same rendering as the previous card rows, including the "(Scheduled)" suffix. */
	const formatEventDate = (event: CareTransitionEvent) => {
		if (!event.dateOn) {
			return Formatter.NO_DATA;
		}

		let finalDate = Formatter.date(event.dateOn.toString(), {
			day: "2-digit",
			month: "2-digit",
			year: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		}) as string;

		if (event.status === "SCHEDULED") {
			finalDate += " (Scheduled)";
		}

		return finalDate;
	};

	if (props.isLoading) {
		return (
			<QuickViewPanelWrapper
				title="PARTICIPANT.DASHBOARD.POST_DISCHARGE.QUICK_VIEW.PANEL_TITLE"
				onClickAction={props.onClickAction}
				isLoading={props.isLoading}
				isError={props.isError}
			>
				<p className="panel-no-data loading">
					<i className="pi pi-spin pi-spinner message-icon"></i>
					{t("PARTICIPANT.DASHBOARD.POST_DISCHARGE.MESSAGES.LOADING")}
				</p>
			</QuickViewPanelWrapper>
		);
	}

	if (props.isError) {
		return (
			<QuickViewPanelWrapper
				title="PARTICIPANT.DASHBOARD.POST_DISCHARGE.QUICK_VIEW.PANEL_TITLE"
				onClickAction={props.onClickAction}
				isLoading={props.isLoading}
				isError={props.isError}
			>
				<p className="panel-no-data has-error">
					{t("PARTICIPANT.DASHBOARD.POST_DISCHARGE.MESSAGES.ERROR")}
				</p>
			</QuickViewPanelWrapper>
		);
	}

	/**
	 * The two header values are summaries derived from discharge events, so with no events
	 * they are necessarily blank. Before KAN-2551 the card still drew both rows as dashes,
	 * which read as a broken card rather than an empty one (Maria, 2026-09-22): the
	 * `NO_DATA` branch only fired when `postDischargeInfo` was entirely absent, and the
	 * API returns an object with null fields instead.
	 */
	const latestAdmissionReason = props.postDischargeInfo?.latestAdmissionReason;
	const daysSinceDischarge = props.postDischargeInfo?.daysSinceDischarge;

	const hasAdmissionReason = Boolean(latestAdmissionReason);
	const hasDaysSinceDischarge = daysSinceDischarge != null;
	const hasHeaderData = hasAdmissionReason || hasDaysSinceDischarge;

	if (events.length === 0 && !hasHeaderData) {
		return (
			<QuickViewPanelWrapper
				title="PARTICIPANT.DASHBOARD.POST_DISCHARGE.QUICK_VIEW.PANEL_TITLE"
				onClickAction={props.onClickAction}
				isLoading={props.isLoading}
				isError={props.isError}
			>
				<p className="panel-no-data">
					{t("PARTICIPANT.DASHBOARD.POST_DISCHARGE.MESSAGES.NO_CARE_TRANSITIONS")}
				</p>
			</QuickViewPanelWrapper>
		);
	}

	return (
		<QuickViewPanelWrapper
			title="PARTICIPANT.DASHBOARD.POST_DISCHARGE.QUICK_VIEW.PANEL_TITLE"
			onClickAction={props.onClickAction}
			isLoading={props.isLoading}
			isError={props.isError}
		>
			<ul className="panel-list">
				{/*
				 * Header: summary values, not events. Each line is omitted entirely when its
				 * value is missing rather than drawn as a dash — a rehab-only participant has
				 * no admission reason and no discharge date, and two dashes above the event
				 * row read as a broken card (Maria, 2026-09-22). Same "no data, no dashes"
				 * rule as the all-empty state above; the difference is that here the card
				 * still has events to show.
				 */}
				{hasAdmissionReason && (
					<li
						key="latest-admission-reason"
						className="panel-list-item"
					>
						<div className="row">
							<span className="item-label">
								{t(
									"PARTICIPANT.DASHBOARD.POST_DISCHARGE.QUICK_VIEW.LATEST_ADMISSION_REASON"
								)}
							</span>
							<span className="item-value">{latestAdmissionReason?.toString()}</span>
						</div>
					</li>
				)}
				{hasDaysSinceDischarge && (
					<li
						key="days_since-discharge"
						className="panel-list-item"
					>
						<div className="row">
							<span className="item-label">
								{t(
									"PARTICIPANT.DASHBOARD.POST_DISCHARGE.QUICK_VIEW.DAYS_SINCE_DISCHARGE"
								)}
							</span>
							<span className="item-value">{daysSinceDischarge?.toString()}</span>
						</div>
					</li>
				)}

				{/* Flat, newest-first event list — or one generic line when there is none */}
				{events.length === 0 && (
					<li
						key="no-care-transitions"
						className="panel-list-item"
					>
						<div className="row">
							<span className="item-value">
								{t(
									"PARTICIPANT.DASHBOARD.POST_DISCHARGE.MESSAGES.NO_CARE_TRANSITIONS"
								)}
							</span>
						</div>
					</li>
				)}
				{events.map((event) => (
					<li
						key={event.id}
						className="panel-list-item"
					>
						<div className="row">
							<span className="item-label">
								{t(`ENUMS.POST_DISCHARGE_PROCEDURES_TYPES.${event.type}`)}
							</span>
							<span className="item-value">{formatEventDate(event)}</span>
						</div>
					</li>
				))}
			</ul>
		</QuickViewPanelWrapper>
	);
};

export default PostDischargeQuickViewPanel;
